import dayjs from "dayjs";
import localeData from "dayjs/plugin/localeData";
import 'dayjs/plugin/localeData'; // <-- This line ensures TypeScript sees the typings
import LnIcon from "components/LnIcon";
import { Button } from 'reactstrap';
import { UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { DayPicker } from 'react-day-picker';
import { sv, da, enGB } from "react-day-picker/locale";
import { useI18n } from "components/lni18n";
import { useState } from "react";
import useTypedSelector from "utils/useTypedSelector";
import 'dayjs/locale/sv';
import 'dayjs/locale/da';
import "react-day-picker/style.css";
import 'assets/scss/react-datepicker-custom.scss';

/**
 * DateTimePicker
 * @module components/DateTimePicker/DateTimePicker
 * @return a Date selected by the user.
 * @param {{
    Date:  Date,
    DatePickerId?: string,
    showMonthDropdown?: boolean,
    showYearDropdown?: boolean,
    showTimeSelect?: boolean,
    showWeekNumbers?: boolean,
    timeCaption?: string, // time
    timeFormat?: string, // HH:mm
    timeIntervals?: number, // {15}
    renderDays: 'all' | 'past' | 'future',
    onDateChanged: Function;
}} props
 */
const DateTimePicker = (props) => {
    const initialDate = props.Date ? props.Date : new Date();
    const [dateValue, setDateValue] = useState(initialDate);
    const { languageService: t } = useI18n();
    const [dropDownDate, setDropDownDate] = useState(initialDate);
    const currentLang = useTypedSelector(state => state.text.currentLang);
    let locale = enGB;

    switch (currentLang.Short) {
        case "sv":
            locale = sv;
            break;
        case "da":
            locale = da;
            break;
        default:
            break;
    }

    dayjs.extend(localeData);
    const globalLocaleData = dayjs().localeData()
    const firstDayOfWeek = globalLocaleData.firstDayOfWeek() as 0 | 1 | 2 | 3 | 4 | 5 | 6;

    const handleTime = (e) => {
        const [hour, minute] = e.target.value.split(":");
        const newDate = dayjs(dateValue).hour(hour).minute(minute).toDate();
        setDateValue(newDate);
        props.onDateChanged(newDate);
    }

    const handleMonthChange = (month) => {
        setDropDownDate(month);
    }

    const handleDateClick = (d) => {
        const nd = setDateState(d);
        setDropDownDate(nd);
    }

    const setstartMonth = (index) => {
        const d = setDateState(dayjs(dropDownDate).month(index).toDate());
        setDropDownDate(d);
    }

    const setstartYear = (y) => {
        const d = setDateState(dayjs(dropDownDate).year(y).toDate());
        setDropDownDate(d)

    }

    const setDateState = (d) => {
        let da = d;
        if (props.showTimeSelect) {
            const oldVal = dayjs(dateValue);
            da = dayjs(d).hour(oldVal.hour()).minute(oldVal.minute()).toDate();
        } else {
            da = dayjs(d).hour(0).minute(0).toDate();
        }

        setDateValue(da);
        props.onDateChanged(da);
        return da;
    }

    // console.debug(day) is there just please the TS compiler
    let disabledDays = (day: Date) => { console.debug(day); return false };

    const now = dayjs().hour(0).minute(1);


    if (props.renderDays === 'past') {
        disabledDays = (day) => now.isBefore(day, 'day');
    }

    if (props.renderDays === 'future') {
        disabledDays = (day) => now.isAfter(day, 'day');
    }

    const years = [];
    const startYear = (new Date()).getFullYear() - 10;
    for (let i = startYear; i <= (startYear + 20); i += 1) {
        years.push(i);
    }

    const setToday = () => {
        const today = new Date();
        setDropDownDate(today);
        setDateState(today); // also fires onDateChanged and preserves time when needed
    }

    /**
     * 
     * @param {Object} prps 
     * @return {React.ReactElement}
     */
    const Navbar = (prps) => {
        return (
            <div className="navbar mt-3 d-flex justify-content-between">
                <button className="btn btn-link" onClick={() => prps.onPreviousClick()}>
                    <LnIcon name="previous" className="mr-2" />
                    {t.getText("previous")}
                </button>
                <button className="btn btn-link" onClick={() => prps.onNextClick()}>
                    {t.getText("next")}
                    <LnIcon className="ml-2" name="next" />
                </button>
            </div>
        );
    }


    return (<>
        {(props.showMonthDropdown || props.showYearDropdown) &&
            <div className="d-flex justify-content-center">
                {props.showMonthDropdown &&
                    <UncontrolledDropdown>
                        <DropdownToggle caret className="btn btn-primary btn-small">
                            {dayjs(dropDownDate).format("MMM")}
                        </DropdownToggle>
                        <DropdownMenu>
                            {dayjs().localeData().months().map((m, i) => <DropdownItem key={i} onClick={() => setstartMonth(i)} >{m}</DropdownItem>)}
                        </DropdownMenu>
                    </UncontrolledDropdown>
                }

                {props.showYearDropdown &&
                    <UncontrolledDropdown className="ml-5">
                        <DropdownToggle caret className="btn btn-primary btn-small">
                            {dayjs(dropDownDate).format("YYYY")}
                        </DropdownToggle>
                        <DropdownMenu >
                            {years.map((y) => <DropdownItem className={y === now.year() ? "font-weight-bold text-success" : ""} key={y} onClick={() => setstartYear(y)} >{y}</DropdownItem>)}
                        </DropdownMenu>
                    </UncontrolledDropdown>
                }
            </div>
        }

        <div className="d-flex m-auto justify-content-center">
            <DayPicker
                mode="single"
                onDayClick={(d) => handleDateClick(d)}
                defaultMonth={dateValue}
                selected={dateValue}
                numberOfMonths={window.outerWidth < 900 ? 1 : 2}
                showOutsideDays={true}
                showWeekNumber={props.showWeekNumbers}
                disabled={disabledDays}
                month={dropDownDate}
                locale={locale}
                ISOWeek={true}
                onMonthChange={handleMonthChange}
                weekStartsOn={firstDayOfWeek}
                components={{
                    Nav: Navbar,
                }}
            />
        </div>

        <div className="d-flex align-items-center justify-content-between">
            <div>&nbsp;</div>
            {!props.showTimeSelect ? <span> {t.getShortDateString(dateValue)}</span> :
                <input
                    type="time"
                    name="time"
                    value={dayjs(dateValue).format("HH:mm")}
                    onChange={(e) => handleTime(e)}
                    step="300" // 5 min
                    className="form-control"
                    style={{ maxWidth: "100px" }}
                    required />
            }

            <Button className={'btn btn-small btn-primary mr-4'} onClick={() => setToday()}>{t.getText("today")}</Button>
        </div>
    </>);
}

export default DateTimePicker;

